import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe, NgClass } from '@angular/common';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { FeatureFlagsGeneration } from '@parashift/shared/constants';
import { ActivatedRoute } from '@angular/router';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { FieldArrayType, FormlyModule } from '@ngx-formly/core';
import { ModalService } from '@parashift/shared/components';
import { CurrentTenantRegister, SessionStore } from '@parashift/shared/services';
import { DocumentTypeSectionConfig } from '../document-type-config';
import { DocumentTypeHelperService } from '../../../services/document-type-helper.service';

@Component({
  selector: 'document-type-sections-configurator',
  templateUrl: './sections-configurator.component.html',
  styleUrls: ['./sections-configurator.component.scss'],
  imports: [
    AsyncPipe,
    FaIconComponent,
    FormlyModule,
    FormsModule,
    NgClass,
    ReactiveFormsModule,
    TooltipModule
]
})
export class DocumentTypeSectionsConfiguratorComponent extends FieldArrayType implements OnInit {
  private route = inject(ActivatedRoute);
  private modalService = inject(ModalService);
  private sessionStore = inject(SessionStore);
  private currentTenantRegister = inject(CurrentTenantRegister);
  documentTypeHelperService = inject(DocumentTypeHelperService);
  private destroyRef = inject<DestroyRef>(DestroyRef);

  canAddSection$: Observable<boolean>;
  private tenant_id: number;

  constructor() {
    super();
  }

  ngOnInit() {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.tenant_id = params.get('tenant_id') ? Number(params.get('tenant_id')) : undefined;
    });
    this.canAddSection$ = this.sessionStore.state$.pipe(
      map(state => state.parashift_powers ||
        this.currentTenantRegister.featureEnabled(FeatureFlagsGeneration.ui_configurations_document_types_fields_allow_add_section) ||
        this.currentTenantRegister.featureEnabled(FeatureFlagsGeneration.ui_configurations_document_types_allow_unlimited_prompt_sections)
      )
    );
  }

  get canAddMoreSections(): boolean {
    if (this.sessionStore.state.parashift_powers) return true;
    if (this.currentTenantRegister.featureEnabled(FeatureFlagsGeneration.ui_configurations_document_types_allow_unlimited_prompt_sections)) return true;
    const activeCount = (this.model || []).filter((s: any) => s.allow_edit).length;
    return activeCount < 5;
  }

  addLlmConfig() {
    this.props.hooks.addLlmConfig();
  }

  removeSection(i) {
    this.modalService
      .confirmation({
        initialState: {
          description: $localize `:@@components.document_type_configurator.document_type_section_delete_description:Do you want to delete this Document Type section?`,
          title: $localize `:@@components.document_type_configurator.document_type_section_delete_title:Delete Document Type section`
        }
      })
      .subscribe(() => {
        const section = this.model[i];
        this.removeDocumentTypeSection(i);
        if (section.id) {
          this.props.hooks.saveConfiguration();
        }
      });
  }

  private removeDocumentTypeSection(i) {
    if (this.props.onRemoveSection) {
      this.props.onRemoveSection(this.model[i], i);
    }
    this.remove(i);
  }

  removeAllSections() {
    for (let i = this.model.length - 1; i >= 0; i--) {
      super.remove(i);
    }
  }

  moveUp(position) {
    const new_position = position - 1;
    const section = this.model[position];

    if (new_position < 0) {
      this.applyModel([...this.model.slice(1, this.model.length), section]);
      return;
    }

    this.replace(position, new_position);
  }

  moveDown(position) {
    const new_position = position + 1;
    const section = this.model[position];

    if (new_position > this.model.length - 1) {
      this.applyModel([section, ...this.model.slice(0, this.model.length - 1)]);
      return;
    }

    this.replace(position, new_position);
  }

  moveToTop(position) {
    const section = this.model[position];
    const newModel = [...this.model];
    newModel.splice(position, 1);
    this.applyModel([section, ...newModel]);
  }

  moveToBottom(position) {
    const section = this.model[position];
    const newModel = [...this.model];
    newModel.splice(position, 1);
    this.applyModel([...newModel, section]);
  }

  add(i?: number, initialModel?: any): void {
    initialModel = initialModel || ({
      title: 'New section',
      fields: [],
      allow_edit: true,
      position: this.model.length
    } as Partial<DocumentTypeSectionConfig>);
    super.add(i, initialModel);
    if (this.props.hooks && this.props.hooks.itemAdded) {
      this.props.hooks.itemAdded(initialModel);
    }
  }

  remove(i: number): void {
    super.remove(i);
  }

  private replace(oldPosition: number, newPosition: number) {
    const reordered = [...this.model];
    const temp = reordered[newPosition];
    reordered[newPosition] = reordered[oldPosition];
    reordered[oldPosition] = temp;
    this.applyModel(reordered);
  }

  private applyModel(newModel: any[]): void {
    while (this.model.length !== 0) {
      this.remove(0);
    }
    for (let i = 0; i < newModel.length; i++) {
      const section = newModel[i];
      section.position = i;
      this.add(undefined, section);
    }
  }
}
